/**
 * TV1-2071 — the ONE rule for reading frames off `backend.subscriptions.AccountsReadmodel`.
 *
 * ── Why this is a shared module and not two inline `if`s ─────────────────────
 *
 * Two independent consumers subscribe to the SAME account channel and both must
 * refresh on the same frames:
 *
 *  · `domains/subscription/state/policies/subscriptionDataPolicy.ts` — refetches the
 *    four `/subscription` reads, which is what makes the amount due settle to zero
 *    and the Pay button unmount on their own (AC-2, screen half).
 *  · `contexts/useAuth/useAuth.tsx#subscribeToAccountSuspension` — re-reads
 *    `/users-profiles/me` so the layout suspension banner clears (AC-2, banner half).
 *
 * They shipped with DIFFERENT filters — one "any frame that is not a snapshot", the
 * other a strict `type === 'signal'`. Not a bug today, because the channel is opened
 * in signal mode and the server sends `{ type: 'signal' }`. But the two halves of one
 * acceptance criterion would diverge the moment the channel delivered anything else,
 * and the divergence would be INVISIBLE: half the screen updates, half does not, with
 * no error and no log. A single predicate is the only way that stays fixed.
 *
 * ── The chosen rule: refresh on ANY frame that is not the initial snapshot ────
 *
 * Both consumers open the subscription in signal mode
 * (`{ _snapshot: false, _changes: 'signal' }`), so `{ type: 'signal' }` — no payload
 * — is what is expected. Filtering strictly on that would make the whole live-update
 * story silently dead if the channel ever delivered deltas instead, and "silently
 * dead" here looks exactly like "nothing changed".
 *
 * The `snapshot` frame IS ignored: both consumers load query-first, so the snapshot
 * carries nothing new. Everything else schedules the debounced refresh. Being
 * generous costs at most one extra round trip against a debounced, idempotent
 * refetch, and buys robustness against a channel-mode change nobody remembered to
 * grep for.
 *
 * ⚠️ A pv3 signal-mode subscription does NOT auto-refetch — the frame is only a
 * trigger. Whatever the predicate admits, the consumer still has to re-READ.
 */

/** One change frame off the account channel. In signal mode there is no payload. */
export type AccountChangeFrame = { type?: string } | null | undefined;

/**
 * Whether a frame means "the account row moved, go re-read it".
 *
 * `false` for an absent frame and for the initial `snapshot`; `true` for everything
 * else, including a bare `{}` and an unknown delta shape.
 */
export const isLiveAccountChangeFrame = ( frame: AccountChangeFrame ): boolean =>
  !!frame && frame.type !== 'snapshot';
